import { BATCH_FUTURE_UID } from "./constants";
import { normalizeClockToHHMM } from "./utils/tierOps";

export const hasAtmostTwoDecimalPlaces = (value) =>
  /^\d*(\.\d{0,2})?$/.test(value);

export const validateOfferName = (offerName) => {
  // Backend is the source of truth on allowed characters (it already stores
  // names like "[Test] Time to Start" and "Early Bird Schedule ₹800", which
  // a strict charset regex would reject and then block every edit save).
  // Only blank names are rejected client-side; anything else travels and a
  // backend 400 surfaces its own message toast.
  if (!offerName || offerName.trim().length === 0) {
    return "Please enter a name for the offer";
  }
  return undefined;
};

/**
 * Single-value price check, direction-aware to mirror the backend
 * (`_final_price_error`): discount must sit below the original price,
 * surge flat is a final price that must sit above it.
 */
export const validateDiscountPrice = (
  discountPrice,
  originalPrice,
  { isSurge = false } = {}
) => {
  let errorDiscount;
  if (!discountPrice) {
    errorDiscount = "Required";
  } else if (!hasAtmostTwoDecimalPlaces(discountPrice)) {
    errorDiscount = "Please add price upto 2 decimals only.";
  } else if (
    originalPrice &&
    !isSurge &&
    parseFloat(discountPrice) >= parseFloat(originalPrice)
  ) {
    errorDiscount = "Price cannot be greater than the original price";
  } else if (
    originalPrice &&
    isSurge &&
    parseFloat(discountPrice) <= parseFloat(originalPrice)
  ) {
    errorDiscount = "Surge price must be greater than the original price";
  }
  return errorDiscount;
};

/**
 * Flat price-change values are final prices (backend `_final_price_error`),
 * not deltas. Validate them against the offering's original price so creators
 * get inline feedback instead of a generic backend 400 on save.
 */
export const validateFlatFinalPrice = (
  value,
  originalPrice,
  { isSurge = false } = {}
) => {
  if (value === "" || value === null || value === undefined) return "Required";
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) {
    return "Enter a price greater than 0";
  }
  if (!originalPrice) return undefined;
  if (isSurge && num <= Number(originalPrice)) {
    return `Price must be greater than the original price (${originalPrice})`;
  }
  if (!isSurge && num >= Number(originalPrice)) {
    return `Price must be less than the original price (${originalPrice})`;
  }
  return undefined;
};

export const validatePercentage = (value, { isSurge = false } = {}) => {
  if (!value) return "Required";
  const num = parseFloat(value);
  // Surge percent only needs to be positive backend-side (no 100 cap).
  if (isNaN(num) || num <= 0 || (!isSurge && num > 100)) {
    return isSurge
      ? "Enter a value greater than 0"
      : "Enter a value between 1 and 100";
  }
};

export const validateEndDate = (end_date, values) => {
  let errorEndDate;
  if (!end_date) {
    errorEndDate = "Required";
  } else if (new Date(end_date) < new Date(values.start_date)) {
    errorEndDate = "End date should be greater than start date";
  }
  return errorEndDate;
};

export const validateDiscountPriceInternational = (
  discountPriceInternational,
  originalPrice,
  { isSurge = false } = {}
) => {
  let errorDiscountInternational;
  if (!discountPriceInternational) {
    errorDiscountInternational = "Required";
  } else if (!hasAtmostTwoDecimalPlaces(discountPriceInternational)) {
    errorDiscountInternational = "Please add price upto 2 decimals only.";
  } else if (
    originalPrice &&
    !isSurge &&
    parseFloat(discountPriceInternational) >= parseFloat(originalPrice)
  ) {
    errorDiscountInternational =
      "Price cannot be greater than the original price";
  } else if (
    originalPrice &&
    isSurge &&
    parseFloat(discountPriceInternational) <= parseFloat(originalPrice)
  ) {
    errorDiscountInternational =
      "Surge price must be greater than the original price";
  }
  return errorDiscountInternational;
};

/**
 * Tier validation (§6): ≥1 row, both cols filled; strictly ascending for
 * count-up triggers, descending for count-down triggers. Inline error string
 * (returned for the `tiers` field) — keeps Create disabled via final-form.
 */
export const validateTiers = (
  tiers = [],
  { countdown = false, requireOrder = true } = {}
) => {
  if (!tiers || tiers.length === 0) return "Add at least one tier";
  for (const t of tiers) {
    if (t.threshold === "" || t.threshold === null || t.threshold === undefined)
      return "Fill threshold for every tier";
    if (t.value === "" || t.value === null || t.value === undefined)
      return "Fill value for every tier";
    if (Number.isNaN(Number(t.threshold)) || Number.isNaN(Number(t.value)))
      return "Tiers must be numbers";
  }
  // Time-proximity tiers are not monotonic (e.g. "30 days, 7 days, 24 hours"),
  // so ordering is only enforced for count-based triggers.
  if (!requireOrder) return undefined;
  const nums = tiers.map((t) => Number(t.threshold));
  for (let i = 1; i < nums.length; i++) {
    if (countdown) {
      if (!(nums[i] < nums[i - 1]))
        return "Thresholds must be strictly descending for this trigger";
    } else if (!(nums[i] > nums[i - 1])) {
      return "Thresholds must be strictly ascending for this trigger";
    }
  }
};

export const validateVipWindows = (rows = []) => {
  if (!rows.length) return "Add at least one window";
  // Form stores batch-creation 12h strings ("02:30 pm"); normalize before
  // comparing so 12h/24h inputs validate identically.
  const toMinutes = (t) => {
    const norm = normalizeClockToHHMM(t);
    if (!norm || typeof norm !== "string" || !norm.includes(":")) return null;
    const [h, m] = norm.split(":");
    const n = Number(h) * 60 + Number(m);
    return Number.isNaN(n) ? null : n;
  };
  // Days/dates with no ranges are allowed (mockup empty state) — at least one
  // row must carry ranges.
  const activeRows = rows.filter((r) => (r.ranges || []).length > 0);
  if (!activeRows.length) return "Add at least one time range to a day or date";
  // Group ranges by row label to catch overlaps across same-day rows too.
  const byLabel = {};
  for (const r of activeRows) {
    if (!r.markup && r.markup !== 0) return "Set markup for every row";
    if (r.label === "") return "Pick a date for every row";
    for (const range of r.ranges) {
      const s = toMinutes(range.start);
      const e = toMinutes(range.end);
      if (s === null || e === null)
        return "Set start and end time for every range";
      if (!(e > s)) return "End time must be after start time";
    }
    const key = r.label || "__unlabeled__";
    byLabel[key] = [...(byLabel[key] || []), ...r.ranges];
  }
  for (const ranges of Object.values(byLabel)) {
    const sorted = ranges
      .map((range) => [toMinutes(range.start), toMinutes(range.end)])
      .sort((a, b) => a[0] - b[0]);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i][0] < sorted[i - 1][1])
        return "Overlapping priority windows — adjust the times";
    }
  }
};

export const validateVipDates = (startDate, { hasActiveRows } = {}) => {
  if (hasActiveRows && !startDate) return "Required";
};

export const validateBatchValues = (rows = []) => {
  const def = rows.find((r) => r.batch_uid === BATCH_FUTURE_UID);
  if (!def || (def.value === "" && def.value !== 0))
    return "Set a value for All future batches (default)";
  // Every listed batch needs its own price — a blank row would silently fall
  // back to the future-batch default at payment, which is not what was set up.
  const hasEmptyBatch = rows.some(
    (r) =>
      r.batch_uid !== BATCH_FUTURE_UID &&
      (r.value === "" || r.value === null || r.value === undefined)
  );
  if (hasEmptyBatch) return "Set a value for every batch";
};
